import { useCallback, useRef, useEffect } from 'react';

/**
 * A hook that provides a debounced callback with leading + trailing edge behavior.
 * 
 * This is ideal for grid row selection where:
 * - The first selection should fire immediately (leading edge)
 * - Subsequent rapid selections (e.g., keyboard scrolling) are debounced
 * - The last selection fires after the debounce delay (trailing edge)
 * 
 * This prevents hundreds of API calls when users scroll through a grid with keyboard.
 * 
 * @param callback - The function to debounce
 * @param delay - Debounce delay in milliseconds (default: 150ms)
 * @returns A debounced version of the callback
 */
export function useLeadingTrailingDebounce<T extends (...args: any[]) => any>(
	callback: T,
	delay: number = 150
): T {
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lastCallRef = useRef<number>(0);
	const lastArgsRef = useRef<Parameters<T> | null>(null);
	const callbackRef = useRef(callback);

	// Keep callback ref updated
	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const debouncedCallback = useCallback(
		(...args: Parameters<T>) => {
			const now = Date.now();
			const timeSinceLastCall = now - lastCallRef.current;

			// Store the latest args for trailing edge
			lastArgsRef.current = args;

			// Clear any existing timeout
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			// Leading edge: fire immediately if enough time has passed
			if (timeSinceLastCall >= delay) {
				lastCallRef.current = now;
				callbackRef.current(...args);
				lastArgsRef.current = null;
			} else {
				// Trailing edge: schedule a call after the delay
				timeoutRef.current = setTimeout(() => {
					if (lastArgsRef.current) {
						lastCallRef.current = Date.now();
						callbackRef.current(...lastArgsRef.current);
						lastArgsRef.current = null;
					}
				}, delay);
			}
		},
		[delay]
	) as T;

	return debouncedCallback;
}
